/**
 * M17 — price⇄pips conversion for /trade/new's SL/TP entry.
 *
 * Client-side on purpose: the server (and MetaApi) only ever see absolute prices. MetaApi's
 * RELATIVE_PIPS unit means the BROKER's pip — a tenth of what a signal channel calls a gold
 * pip — so a pip count must never cross the wire (the trade.ts scar). The pipSize used here
 * comes from GET /api/symbols, where the server pre-resolved the broker's own value.
 */

export type PipsContext = {
  direction: "buy" | "sell";
  /** Market buy: the ask. Market sell: the bid. Pending: the typed entry price. */
  referencePrice: number;
  pipSize: number;
  digits: number | null;
};

/** Which way each level sits from the reference: SL against the trade, TP with it. */
function sign(kind: "sl" | "tp", direction: "buy" | "sell"): 1 | -1 {
  const away = kind === "sl" ? -1 : 1;
  return direction === "buy" ? (away as 1 | -1) : ((-away) as 1 | -1);
}

export function priceFromPips(kind: "sl" | "tp", pips: number, ctx: PipsContext): number {
  const raw = ctx.referencePrice + sign(kind, ctx.direction) * pips * ctx.pipSize;
  return ctx.digits === null ? raw : Number(raw.toFixed(ctx.digits));
}

/**
 * The reverse: measure a typed price in pips. `wrongSide` flags a level on the wrong side
 * of the reference (a buy SL above the market) — flagged for the UI to warn about, never
 * silently flipped: the typed price stays exactly what the user wrote.
 */
export function pipsFromPrice(
  kind: "sl" | "tp",
  price: number,
  ctx: PipsContext,
): { pips: number; wrongSide: boolean } {
  const delta = price - ctx.referencePrice;
  const pips = Number((Math.abs(delta) / ctx.pipSize).toFixed(1));
  const wrongSide = delta !== 0 && Math.sign(delta) !== sign(kind, ctx.direction);
  return { pips, wrongSide };
}
